import {
  MineModelDto,
  ProjectDto,
  RheologyDataDto,
  RheologyDataSummaryDto,
  StopeBackfillSummaryDto,
  StopeLocationDto,
  useMineModelRheologyDataListQuery
} from 'providers/api';
import { enqueueSnackbar } from 'notistack';
import { trackEvent } from 'providers/analytics';
import React, { useRef, useState, useMemo } from 'react';
import { LOCAL_STORAGE_PREFIX } from 'config';
import { DEFAULT_STOPE_SHAPE_OPACITY } from '../MineModel/Meshes/stopeShapeColours';
import PCNode from '../MineModel/Nodes/PCNode';
import PCPipe from '../MineModel/Pipes/PCPipe';
import PCRoute from '../MineModel/Route/PCRoute';
import PCSpool from '../MineModel/Spools/PCSpool';
import * as GConstants from '../MineModel/GConstants';
import * as PCConstants from '../MineModel/PCConstants';
import PCStope from '../MineModel/Stopes/PCStope';
import { initNodes } from '../MineModel/Nodes';
import { loadPipes } from '../MineModel/Pipes';
import { loadSpools, pairKey } from '../MineModel/Spools';
import { initStopes } from '../MineModel/Stopes';
import { generateAllRoutes, routeOptsForProject, returnShortestRoute } from '../MineModel/Route/utils';
import { describeRouteFailure, routeFailureKey } from '../MineModel/Route/errors';
import { PasteOptimiseResult, RecipeState, StopeFillState } from '../MineModel/types';
import Vector from '../MineModel/Nodes/Vector';
import { calculateFrictionForRoute } from '../canvas/Model/updateFriction';
import { ImportedSolid } from '../MineModel/ImportedSolids/ImportedSolid';
import { ClippingBounds } from '../MineModel/clippingBounds';
import {
  filterStopesByLevelArea,
  isLevelAreaFilterActive,
  LevelAreaFilter,
  NO_LEVEL_AREA_FILTER
} from '../MineModel/levelAreaFilter';
import { NO_SPOOL_DATE_FILTER, SpoolDateFilter } from '../MineModel/spoolDateFilter';
import { StopeShapeDateRange } from '../MineModel/Meshes/stopeShapeDateRange';
import { AnimationStep, StopePourData } from '../MineModel/Meshes/stopePourTimeline';
import { getActiveStopeLegendConfig } from '../Components/StopeLegendBuilder/stopeLegend';
import type { StopeLegendConfig } from '../Components/StopeLegendBuilder/stopeLegend';
import { TunnelProfile, buildTunnelProfileFromDto } from '../MineModel/TunnelProfile/TunnelProfile';

const STOPE_SHAPE_OPACITY_STORAGE_KEY = `${LOCAL_STORAGE_PREFIX}-stope-shape-opacity`;
const LEVEL_AREA_STOPES_FILTER = 'levelAreaFilter';

const clampStopeShapeOpacity = (value: number): number => Math.max(0, Math.min(1, value));

const loadStopeShapeOpacity = (): number => {
  const stored = window.localStorage.getItem(STOPE_SHAPE_OPACITY_STORAGE_KEY);
  if (stored === null) return DEFAULT_STOPE_SHAPE_OPACITY;
  const parsed = Number(stored);
  return Number.isFinite(parsed) ? clampStopeShapeOpacity(parsed) : DEFAULT_STOPE_SHAPE_OPACITY;
};

const getShowStopeShapesStorageKey = (projectId: string) => `${projectId}-showStopeShapes`;
const getShowInactiveSpoolsStorageKey = (projectId: string) => `${projectId}-showInactiveSpools`;
const getTunnelProfileVisibleStorageKey = (projectId: string) => `${projectId}-tunnelProfileVisible`;

const loadStoredFlag = (key: string): boolean | null => {
  try {
    const stored = window.localStorage.getItem(key);
    return stored === null ? null : stored === 'true';
  } catch {
    return null;
  }
};

const storeFlag = (key: string, value: boolean | null) => {
  try {
    if (value === null) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, String(value));
  } catch {
    // localStorage unavailable — the choice simply won't persist
  }
};

type ModelPart = 'nodes' | 'stopes' | 'spools' | 'routes' | 'pipes';
type FilterContext = {
  routes?: Record<string, PCRoute>;
  nodes?: Record<string, PCNode>;
  stopes?: Record<string, PCStope[]>;
  spools?: Record<string, PCSpool>;
  pipes?: Record<string, PCPipe>;
};
type ModelFilter<T> = (origionalDictionary: Record<string, T>, context?: FilterContext) => Record<string, T>;

interface Model {
  nodes: Record<string, PCNode>;
  stopes: Record<string, PCStope[]>;
  spools: Record<string, PCSpool>;
  routes: Record<string, PCRoute>;
  pipes: Record<string, PCPipe>;
  importedSolids: Record<string, ImportedSolid>;
  tunnelProfile: TunnelProfile | null;
  clippingBounds: ClippingBounds | null;
  showClippingBounds: boolean;
  showStopeShapes: boolean;
  showInactiveSpools: boolean;
  levelAreaFilter: LevelAreaFilter;
  spoolDateFilter: SpoolDateFilter;
  stopeShapeDateRange: StopeShapeDateRange | null;
  stopeShapeTimelineDay: number | null;
  stopeShapeOpacity: number;
  stopeShapeAnimationStep: AnimationStep;
  stopePourData: StopePourData;
  stopeLegendConfig: StopeLegendConfig | null;
  stopeFillState: StopeFillState | null;
  stopeLocations: StopeLocationDto[] | undefined;
  mineOverview: StopeBackfillSummaryDto[];
  project: ProjectDto;
  selectedStope?: PCStope | undefined;
  selectedNode?: PCNode | undefined;
  selectedSpool?: PCSpool | undefined;
  activeBackfillPlant: PCNode;
  rheology: RheologyDataDto[];
  rheologySummaries: RheologyDataSummaryDto[];
  recipeState: RecipeState | undefined;
  isUpdating: boolean;
  // A filter (an isolate, say) hides nodes, spools or pipes, so a route search would see an incomplete network.
  isNetworkFiltered: boolean;
  optimiserResults: PasteOptimiseResult | undefined;
  // Bumped whenever initModelState rebuilds the scene objects, which discards any appearance painted onto them.
  loadGeneration: number;
  addFilter: (modelpart: ModelPart, filterName: string, filter: ModelFilter<any>) => void;
  removeFilter: (modelpart: ModelPart, filterName: string) => void;
  setRoutes: (routes: Record<string, PCRoute>) => void;
  setSelectedRoute: (route: PCRoute | undefined) => void;
  getSelectedRoute: () => PCRoute | undefined;
  setSelectedStope: (stope: PCStope | undefined) => void;
  unsetSelectedStope: () => void;
  setSelectedNode: (node: PCNode | undefined) => void;
  setSelectedSpool: (spool: PCSpool | undefined) => void;
  setActiveBackfillPlant: (backfillPlant: PCNode) => void;
  showPipes: boolean;
  setSpoolColoursByPipe: (show: boolean) => void;
  resolveSpoolBaseColour: (spool: PCSpool) => string;
  clearAllPipeColors: () => void;
  setNodeProperty: <K extends keyof PCNode>(nodeId: string, property: K, value: PCNode[K]) => void;
  setSpoolProperty: <K extends keyof PCSpool>(spoolId: string, property: K, value: PCSpool[K]) => void;
  setNodeProperties: (nodeId: string, properties: Partial<PCNode>) => void;
  setSpoolProperties: (spoolId: string, properties: Partial<PCSpool>) => void;
  setNodeColour: (nodeId: string, colour: string) => void;
  setNodeColours: (nodeIds: string[], colour: string) => void;
  setSpoolColour: (spoolId: string, colour: string) => void;
  setNodeSize: (nodeId: string, size: number) => void;
  batchUpdateNodeAppearance: (updates: Array<{ nodeId: string; nodeColour: string; nodeSize: number }>) => void;
  batchUpdateSpoolAppearance: (updates: Array<{ spoolId: string; spoolColour: string }>) => void;
  setSpoolInspectionPoints: (inspectionPointsBySpoolId: Record<string, string>) => void;
  clearAllInspectionPoints: () => void;
  setNodeConstraint: (nodeId: string, constrained: boolean) => void;
  setSpoolConstraint: (spoolId: string, constrained: boolean) => void;
  unsetNodeAsStope: (nodeId: string) => void;
  unsetNodeAsPit: (nodeId: string) => void;
  setNodeAsStope: (nodeId: string) => void;
  setNodeAsPit: (nodeId: string) => void;
  setRecipeState: (recipeState: RecipeState | undefined) => void;
  setOptimiserResults: (optimiserResults: PasteOptimiseResult | undefined) => void;
  findSpoolsByNodeId: (spools: PCSpool[], nodeId: string) => PCSpool[];
  findSpoolByPair: (nodeAId: string, nodeBId: string) => PCSpool | undefined;
  findNodeById: (nodeId: string) => PCNode | undefined;
  addNode: (node: PCNode) => void;
  addNodes: (nodes: PCNode[]) => void;
  addSpool: (node: PCSpool) => void;
  addSpools: (spools: PCSpool[]) => void;
  removeNodes: (nodes: PCNode[]) => void;
  removeSpools: (spoolsToRemove: PCSpool[]) => void;
  addImportedSolid: (solid: ImportedSolid) => void;
  removeImportedSolid: (guid: string) => void;
  clearImportedSolids: () => void;
  setTunnelProfile: (profile: TunnelProfile | null) => void;
  setTunnelProfileVisible: (visible: boolean) => void;
  setTunnelProfileOpacity: (opacity: number) => void;
  setClippingBounds: (bounds: ClippingBounds | null) => void;
  setShowClippingBounds: (show: boolean) => void;
  setShowStopeShapes: (show: boolean) => void;
  setShowInactiveSpools: (show: boolean) => void;
  setLevelAreaFilter: (filter: LevelAreaFilter) => void;
  setSpoolDateFilter: (filter: SpoolDateFilter) => void;
  setStopeShapeDateRange: (range: StopeShapeDateRange | null) => void;
  setStopeShapeTimelineDay: (epochDay: number | null) => void;
  setStopeShapeOpacity: (opacity: number) => void;
  setStopeShapeAnimationStep: (step: AnimationStep) => void;
  setStopePourData: (data: StopePourData) => void;
  setStopeLegendConfig: (config: StopeLegendConfig | null) => void;
  setStopeFillState: (state: StopeFillState | null) => void;
}

interface ModelState {
  model: Model;
  initModelState: (project: ProjectDto, mineModel: MineModelDto, mineOverview: StopeBackfillSummaryDto[]) => void;
  updateMineOverview: (mineOverview: StopeBackfillSummaryDto[]) => void;
  loaded: () => boolean;
}

const useModelState = (): ModelState => {
  const [nodes, setNodes] = useState<Record<string, PCNode>>();
  const [stopes, setStopes] = useState<Record<string, PCStope[]>>();
  const [spools, setSpools] = useState<Record<string, PCSpool>>();
  const [routes, setRoutes] = useState<Record<string, PCRoute>>();
  const [pipes, setPipes] = useState<Record<string, PCPipe>>();
  const [stopeLocations] = useState<StopeLocationDto[]>();

  const [mineOverview, setMineOverview] = useState<StopeBackfillSummaryDto[]>();
  const [project, setProject] = useState<ProjectDto>();
  const [rheologySummaries, setRheologySummaries] = useState<RheologyDataSummaryDto[]>();

  const [selectedStope, setSelectedStope] = useState<PCStope | undefined>();
  const [selectedNode, setSelectedNode] = useState<PCNode | undefined>();
  const [selectedSpool, setSelectedSpool] = useState<PCSpool | undefined>();
  const [activeBackfillPlant, setActiveBackfillPlant] = useState<PCNode | undefined>();
  const [recipeState, setRecipeState] = useState<RecipeState | undefined>();
  const [optimiserResults, setOptimiserResults] = useState<PasteOptimiseResult | undefined>();

  const [importedSolids, setImportedSolids] = useState<Record<string, ImportedSolid>>({});
  const [tunnelProfile, setTunnelProfileState] = useState<TunnelProfile | null>(null);
  const [clippingBounds, setClippingBoundsState] = useState<ClippingBounds | null>(null);
  const [showClippingBounds, setShowClippingBoundsState] = useState<boolean>(false);
  const [showStopeShapes, setShowStopeShapesState] = useState<boolean>(false);
  const [showInactiveSpools, setShowInactiveSpoolsState] = useState(true);
  const [levelAreaFilter, setLevelAreaFilterState] = useState<LevelAreaFilter>(NO_LEVEL_AREA_FILTER);
  // Render-only, like level and area: routing and recipes still read every spool.
  const [spoolDateFilter, setSpoolDateFilter] = useState<SpoolDateFilter>(NO_SPOOL_DATE_FILTER);
  const [stopeShapeDateRange, setStopeShapeDateRange] = useState<StopeShapeDateRange | null>(null);
  const [stopeShapeTimelineDay, setStopeShapeTimelineDay] = useState<number | null>(null);
  const [stopeShapeOpacity, setStopeShapeOpacityState] = useState<number>(loadStopeShapeOpacity);
  const [stopeShapeAnimationStep, setStopeShapeAnimationStepState] = useState<AnimationStep>('day');
  const [stopePourData, setStopePourDataState] = useState<StopePourData>({});
  const [stopeLegendConfig, setStopeLegendConfig] = useState<StopeLegendConfig | null>(null);
  const [stopeFillState, setStopeFillStateInternal] = useState<StopeFillState | null>(null);
  const [loadGeneration, setLoadGeneration] = useState(0);

  const isUpdatingRef = useRef(false);
  const selectedStopeRef = useRef<PCStope | undefined>(undefined);
  const showPipesRef = useRef(false);
  const [showPipes, setShowPipes] = useState(false);

  const [modelFilters, setModelFilters] = useState<Record<ModelPart, Record<string, ModelFilter<any>>>>({
    nodes: {},
    stopes: {},
    spools: {},
    routes: {},
    pipes: {},
  });

  const getStorageKey = (projectId: string) => `${projectId}-backFillPlantNodeId`;

  // Fetch full rheology data when summaries are available
  const rheologyDataSetIds = rheologySummaries?.map((summary) => summary.rheologyDataSetId) || [];
  const { data: fullRheologyData } = useMineModelRheologyDataListQuery(
    {
      mineModelId: project?.entityId || '',
      rheologyDataSetIds,
    },
    {
      enabled: !!project?.entityId && rheologyDataSetIds.length > 0,
    },
  );

  const initModelState = (projectData: ProjectDto, mineModel: MineModelDto, mineOverviewData: StopeBackfillSummaryDto[]) => {
    // Handle case where reticulation data is not available yet
    const { reticulationData } = mineModel;
    const initialisedNodes = reticulationData ? initNodes(reticulationData) : {};
    const initialisedStopes = reticulationData
      ? initStopes(reticulationData.stopeLocations, reticulationData.stopes, initialisedNodes)
      : {};
    const initialisedPipes = reticulationData ? loadPipes(reticulationData.pipeData) : {};
    const initialisedSpools = reticulationData
      ? loadSpools(initialisedNodes, initialisedPipes, reticulationData.spools)
      : {};
    const initialisedMineOverview = mineOverviewData;
    const initialisedProject = projectData;
    const initialisedRheologySummaries = mineModel.rheologyDataList || [];
    const activeStopeLegend = getActiveStopeLegendConfig(projectData.entityId);
    const storageKey = getStorageKey(projectData.entityId);
    const storedNodeId = localStorage.getItem(storageKey);

    let initialBackfillPlant: PCNode;

    if (Object.keys(initialisedNodes).length > 0) {
      const nodeValues = Object.values(initialisedNodes);
      const storedNode = storedNodeId ? initialisedNodes[storedNodeId] : undefined;

      // 1) Prefer the stored node while it is still a plant. A stored non-plant is the arbitrary
      // fallback below from a visit before any plant was set; honouring it would keep routing from
      // that node after the real plant is marked.
      if (storedNode?.isPlantNode) {
        initialBackfillPlant = storedNode;
      } else {
        // isPlantNode is authoritative; the self-referencing-root fallback no longer exists.
        const firstPlant = nodeValues.find((node) => node.isPlantNode);
        initialBackfillPlant = firstPlant || nodeValues[0];

        if (firstPlant) {
          localStorage.setItem(storageKey, firstPlant.id);
        } else {
          localStorage.removeItem(storageKey);
        }
      }
    } else {
      // Create a default backfill plant when no nodes are available
      initialBackfillPlant = new PCNode({
        coords: new Vector(0, 0, 0),
        id: 'default-plant',
        pipeId: 'default-pipe',
        isPlantNode: true,
        area: 'default',
        level: 'default',
        pit: 'default',
        nodeColour: 'default',
        nodeSize: 1,
      });
    }

    if (showPipesRef.current) {
      Object.entries(initialisedSpools).forEach(([, spool]) => {
        const parsed = parseInt(spool.pipe.pipeId as unknown as string, 10);
        const idx = Number.isFinite(parsed) ? Math.max(0, parsed - 1) : 0;
        const pipeColour = spool.pipe.colour || GConstants.PIPE_COLOURS[idx] || PCConstants.SPOOL_COLOUR;
        spool.changeColour(pipeColour);
      });
    }

    // Load the persisted, mine-wide tunnel profile from the model DTO. Re-use the already-loaded
    // profile (by stored file path) so model refreshes don't re-fetch geometry or re-frame the camera.
    const incomingTunnelPath = mineModel.tunnelProfile?.geometryFile?.path ?? null;
    const isSameProject = project?.entityId === projectData.entityId;
    const currentTunnelPath = tunnelProfile?.sourceName ?? null;
    const storedTunnelVisible = loadStoredFlag(getTunnelProfileVisibleStorageKey(projectData.entityId));

    if (!isSameProject) {
      setShowStopeShapesState(loadStoredFlag(getShowStopeShapesStorageKey(projectData.entityId)) ?? false);
      setShowInactiveSpoolsState(loadStoredFlag(getShowInactiveSpoolsStorageKey(projectData.entityId)) ?? true);
    }

    if (!incomingTunnelPath) {
      if (tunnelProfile) setTunnelProfileState(null);
    } else if (isSameProject && tunnelProfile && currentTunnelPath === incomingTunnelPath) {
      setTunnelProfileState((prev) => (prev ? {
        ...prev,
        opacity: mineModel.tunnelProfile?.opacity ?? prev.opacity,
        visible: storedTunnelVisible ?? mineModel.tunnelProfile?.visible ?? prev.visible,
      } : prev));
    } else {
      buildTunnelProfileFromDto(mineModel.tunnelProfile).then((built) => {
        if (built) setTunnelProfileState({ ...built, visible: storedTunnelVisible ?? built.visible });
      });
    }

    const shouldRefreshSelection = project?.entityId === projectData.entityId;
    const refreshedSelectedNode = shouldRefreshSelection && selectedNode?.id
      ? initialisedNodes[selectedNode.id]
      : undefined;
    const selectedStopeId = shouldRefreshSelection ? selectedStope?.data?.stopeId : undefined;
    const refreshedSelectedStope = selectedStopeId
      ? Object.values(initialisedStopes)
        .flat()
        .find((stope) => stope?.data?.stopeId === selectedStopeId)
      : undefined;

    let refreshedRoutes: Record<string, PCRoute> | undefined;
    let noRoutesVisitedSpools: PCSpool[] = [];
    if (selectedStopeId) {
      if (refreshedSelectedStope?.originNode) {
        try {
          const { routeObjects: newRoutes, visitedSpools } = generateAllRoutes(
            initialBackfillPlant,
            initialisedSpools,
            refreshedSelectedStope,
            Object.values(initialisedPipes),
            routeOptsForProject(projectData),
            initialisedNodes,
            initialisedPipes,
          );
          Object.values(newRoutes).forEach((route) => {
            route.changeSegmentColours([]);
          });
          refreshedRoutes = newRoutes;
          noRoutesVisitedSpools = newRoutes && Object.values(newRoutes).length === 0
            ? visitedSpools
            : [];
        } catch (error) {
          refreshedRoutes = {};
          enqueueSnackbar(describeRouteFailure(error), { variant: 'error', key: routeFailureKey(error) });
        }
      } else {
        refreshedRoutes = {};
      }
    }

    noRoutesVisitedSpools.forEach((spool) => {
      spool.changeColour(PCConstants.FRICTIONLOOP_COLOR);
    });

    setNodes(initialisedNodes);
    setStopes(initialisedStopes);
    setPipes(initialisedPipes);
    setSpools(initialisedSpools);
    setMineOverview(initialisedMineOverview);
    setProject(initialisedProject);
    setRheologySummaries(initialisedRheologySummaries);
    setStopeLegendConfig(activeStopeLegend);
    setActiveBackfillPlant(initialBackfillPlant);
    setLoadGeneration((generation) => generation + 1);
    if (shouldRefreshSelection && selectedNode?.id) {
      setSelectedNode(refreshedSelectedNode);
    }
    if (selectedStopeId) {
      selectedStopeRef.current = refreshedSelectedStope;
      setSelectedStope(refreshedSelectedStope);
      setOptimiserResults(undefined);
      setRoutes(refreshedRoutes ?? {});
    }
  };

  const loaded = () => !!spools;

  const addFilter = (modelpart: ModelPart, filterName: string, filter: ModelFilter<any>) => {
    setModelFilters((prev) => ({
      ...prev,
      [modelpart]: {
        ...prev[modelpart],
        [filterName]: filter,
      },
    }));
  };

  const removeFilter = (modelpart: ModelPart, filterName: string) => {
    setModelFilters((prev) => {
      const updatedModelPartFilters = { ...(prev[modelpart] ?? {}) };
      delete updatedModelPartFilters[filterName];
      return {
        ...prev,
        [modelpart]: updatedModelPartFilters,
      };
    });
  };

  // Lives on the model, not the stope list, so it stays on in every panel until changed or cleared by Show All.
  const setLevelAreaFilter = (filter: LevelAreaFilter) => {
    setLevelAreaFilterState(filter);
    if (isLevelAreaFilterActive(filter)) {
      addFilter('stopes', LEVEL_AREA_STOPES_FILTER, filterStopesByLevelArea(filter));
    } else {
      removeFilter('stopes', LEVEL_AREA_STOPES_FILTER);
    }
  };

  const applyFilters = <T, >(modelpart: ModelPart, originalDictionary: Record<string, T>, context?: FilterContext): Record<string, T> => {
    if (!modelFilters) return originalDictionary;
    let newDictionary = { ...originalDictionary };
    Object.entries(modelFilters[modelpart] || {}).forEach(([, filter]) => {
      newDictionary = filter(newDictionary, context);
    });
    return newDictionary;
  };

  const filteredRoutes = applyFilters('routes', routes || {});
  const filteredNodes = applyFilters('nodes', nodes || {}, { routes: filteredRoutes });
  const filteredStopes = applyFilters('stopes', stopes || {}, { routes: filteredRoutes, nodes: filteredNodes });
  const filteredSpools = applyFilters('spools', spools || {}, { routes: filteredRoutes });
  const filteredPipes = applyFilters('pipes', pipes || {});

  const handleSetActiveBackfillPlant = (backfillPlant: PCNode) => {
    setActiveBackfillPlant(backfillPlant);
    if (project && backfillPlant) {
      const storageKey = getStorageKey(project.entityId);
      localStorage.setItem(storageKey, backfillPlant.id);
    }
  };

  React.useEffect(() => {
    if (!nodes || Object.keys(nodes).length === 0) return;
    if (activeBackfillPlant && nodes[activeBackfillPlant.id]) return;

    const plantNodes = Object.values(nodes).filter((node) => node.isPlantNode);
    const firstPlant = plantNodes[0];
    if (firstPlant) {
      handleSetActiveBackfillPlant(firstPlant);
    }
  }, [nodes, activeBackfillPlant, project]);

  const handleSetRecipeState = (newRecipeState: RecipeState | undefined) => {
    setRecipeState(newRecipeState);
  };

  const resolvePipeColour = (spool: PCSpool): string => {
    const parsed = parseInt(spool.pipe.pipeId as unknown as string, 10);
    const idx = Number.isFinite(parsed) ? Math.max(0, parsed - 1) : 0;
    return spool.pipe.colour || GConstants.PIPE_COLOURS[idx] || PCConstants.SPOOL_COLOUR;
  };

  const handleSetSelectedStope = (stope: PCStope | undefined) => {
    // Only a genuine change of stope counts — programmatic re-selection (e.g. data refresh) is skipped.
    if (stope && stope.data.stopeId !== selectedStope?.data.stopeId) {
      trackEvent('stope_selected', {
        stope_id: stope.data.stopeId,
        project_id: project?.entityId,
      });
    }
    isUpdatingRef.current = true;
    selectedStopeRef.current = stope;
    setSelectedStope(stope);
    setOptimiserResults(undefined);
    if (stope && activeBackfillPlant && spools && pipes && nodes) {
      const baseColour = (spool: PCSpool) => (showPipesRef.current ? resolvePipeColour(spool) : PCConstants.SPOOL_COLOUR);
      Object.values(spools).forEach((spool) => {
        spool.changeColour(baseColour(spool));
      });
      if (stope.originNode) {
        try {
          const { routeObjects: newRoutes, visitedSpools } = generateAllRoutes(
            activeBackfillPlant,
            spools,
            stope,
            Object.values(pipes),
            routeOptsForProject(project),
            nodes,
            pipes,
          );
          Object.values(newRoutes).forEach((route) => {
            route.changeSegmentColours([]);
          });
          setRoutes(newRoutes);
          if (newRoutes && Object.values(newRoutes).length === 0) {
            visitedSpools.forEach((spool) => {
              spool.changeColour(PCConstants.FRICTIONLOOP_COLOR);
            });
            enqueueSnackbar('No routes found', { variant: 'error' });
          }
        } catch (error) {
          setRoutes({});
          enqueueSnackbar(describeRouteFailure(error), { variant: 'error', key: routeFailureKey(error) });
        }
      } else {
        setRoutes({});
      }
    }
    setTimeout(() => { isUpdatingRef.current = false; }, 0);
  };

  const handleSelectedRoute = (route?: PCRoute) => {
    setRoutes((prev) => {
      if (!prev) return prev;

      const next = { ...prev };

      Object.keys(next).forEach((k) => {
        const r = next[k] as PCRoute;
        if (r) r.selected = false;
      });

      if (!route) return next;

      // setRoutes is functional, so a late call lands on the current map: without this a route from
      // the stope we just moved off is merged in under its own origin node, where it wins both
      // getSelectedRoute and returnShortestRoute.
      const currentStopeId = selectedStopeRef.current?.data?.stopeId;
      if (currentStopeId && route.stope?.data?.stopeId !== currentStopeId) return next;

      const nodeId = route.originNode?.id;
      if (!nodeId) return next;

      const clonedRoute: PCRoute = Object.create(Object.getPrototypeOf(route)) as PCRoute;
      Object.assign(clonedRoute, route);
      clonedRoute.selected = true;

      next[nodeId] = clonedRoute;

      return next;
    });
  };

  const getSelectedRoute = (): PCRoute | undefined => {
    const list = Object.values(filteredRoutes || {});
    return list.find((r) => r.selected);
  };

  const handleSetSelectedNode = (node: PCNode | undefined) => {
    setSelectedNode(node);
  };

  // Spool ids are opaque GUIDs, so membership is decided purely on the endpoints.
  const findSpoolsByNodeId = (spoolsList: PCSpool[], nodeId: string) => spoolsList
    .filter((spool) => spool.pointA.id === nodeId || spool.pointB.id === nodeId);

  const spoolsByPairKey = useMemo(
    () => Object.values(spools || {}).reduce((acc, spool) => acc.set(spool.pairKey(), spool), new Map<string, PCSpool>()),
    [spools],
  );

  const findSpoolByPair = (nodeAId: string, nodeBId: string) => spoolsByPairKey.get(pairKey(nodeAId, nodeBId));

  const findNodeById = (nodeId: string) => nodes?.[nodeId] || undefined;

  const setNodeProperty = <K extends keyof PCNode>(nodeId: string, property: K, value: PCNode[K]) => {
    setNodes((prev) => {
      if (!prev) return prev;
      const existing = prev[nodeId];
      if (!existing) return prev;
      const updatedNode: PCNode = { ...existing, [property]: value } as PCNode;
      return { ...prev, [nodeId]: updatedNode };
    });
  };

  const setSpoolProperty = <K extends keyof PCSpool>(spoolId: string, property: K, value: PCSpool[K]) => {
    setSpools((prev) => {
      if (!prev) return prev;
      const existing = prev[spoolId];
      if (!existing) return prev;

      (existing as any)[property] = value;

      return { ...prev, [spoolId]: existing };
    });
  };

  const setNodeProperties = (nodeId: string, properties: Partial<PCNode>) => {
    setNodes((prev) => {
      if (!prev) return prev;
      const existing = prev[nodeId];
      if (!existing) return prev;
      const updatedNode: PCNode = { ...existing, ...properties } as PCNode;
      return { ...prev, [nodeId]: updatedNode };
    });
  };

  const setSpoolProperties = (spoolId: string, properties: Partial<PCSpool>) => {
    setSpools((prev) => {
      if (!prev) return prev;
      const existing = prev[spoolId];
      if (!existing) return prev;

      Object.assign(existing as any, properties);

      return { ...prev, [spoolId]: existing };
    });
  };

  const unsetNodeAsStope = (nodeId: string) => {
    setNodeProperty(nodeId, 'isStope', false);
    setStopes((prev) => {
      if (!prev) return prev;
      if (!(nodeId in prev)) return prev;
      const newStopes = { ...prev };
      delete newStopes[nodeId];
      return newStopes;
    });
  };

  const unsetNodeAsPit = (nodeId: string) => setNodeProperty(nodeId, 'isPit', false);
  const setNodeAsStope = (nodeId: string) => setNodeProperties(nodeId, { isStope: true, isPit: false });
  const setNodeAsPit = (nodeId: string) => setNodeProperties(nodeId, { isStope: false, isPit: true });
  const setNodeColour = (nodeId: string, colour: string) => setNodeProperty(nodeId, 'nodeColour', colour);
  const setNodeColours = (nodeIds: string[], colour: string) => {
    if (nodeIds.length === 0) return;

    setNodes((prev) => {
      if (!prev) return prev;

      const updated = { ...prev };
      let hasChanges = false;

      nodeIds.forEach((nodeId) => {
        const existing = updated[nodeId];
        if (!existing || existing.nodeColour === colour) return;
        updated[nodeId] = { ...existing, nodeColour: colour } as PCNode;
        hasChanges = true;
      });

      return hasChanges ? updated : prev;
    });
  };
  const setSpoolColour = (spoolId: string, colour: string) => setSpoolProperty(spoolId, 'spoolColour', colour);
  const setNodeSize = (nodeId: string, size: number) => setNodeProperty(nodeId, 'nodeSize', size);

  const batchUpdateNodeAppearance = (updates: Array<{ nodeId: string; nodeColour: string; nodeSize: number }>) => {
    if (updates.length === 0) return;
    setNodes((prev) => {
      if (!prev) return prev;
      const next = { ...prev };
      let hasChanges = false;
      updates.forEach(({ nodeId, nodeColour, nodeSize }) => {
        const existing = next[nodeId];
        if (!existing) return;
        if (existing.nodeColour === nodeColour && existing.nodeSize === nodeSize) return;
        next[nodeId] = { ...existing, nodeColour, nodeSize } as PCNode;
        hasChanges = true;
      });
      return hasChanges ? next : prev;
    });
  };

  const batchUpdateSpoolAppearance = (updates: Array<{ spoolId: string; spoolColour: string }>) => {
    if (updates.length === 0) return;
    setSpools((prev) => {
      if (!prev) return prev;
      const next = { ...prev };
      let hasChanges = false;
      updates.forEach(({ spoolId, spoolColour }) => {
        const existing = next[spoolId];
        if (!existing || (existing as any).spoolColour === spoolColour) return;
        (existing as any).spoolColour = spoolColour;
        next[spoolId] = existing;
        hasChanges = true;
      });
      return hasChanges ? next : prev;
    });
  };

  // An inspection point measures a spool, so the marker is a spool annotation. Spools are class
  // instances with methods, so they are mutated in place (as batchUpdateSpoolAppearance does) rather
  // than spread, which would drop the prototype.
  const setSpoolInspectionPoints = (inspectionPointsBySpoolId: Record<string, string>) => {
    setSpools((prev) => {
      if (!prev) return prev;
      const next = { ...prev };
      Object.values(next).forEach((spool) => {
        const name = inspectionPointsBySpoolId[spool.spoolId];
        const isInspectionPoint = name !== undefined;
        Object.assign(spool, { isInspectionPoint, inspectionPointName: isInspectionPoint ? name : undefined });
      });
      return next;
    });
  };

  const clearAllInspectionPoints = () => {
    setSpoolInspectionPoints({});
  };

  const addImportedSolid = (solid: ImportedSolid) => {
    setImportedSolids((prev) => ({ ...prev, [solid.guid]: solid }));
  };

  const removeImportedSolid = (guid: string) => {
    setImportedSolids((prev) => {
      if (!(guid in prev)) return prev;
      const next = { ...prev };
      delete next[guid];
      return next;
    });
  };

  const clearImportedSolids = () => setImportedSolids({});

  const setTunnelProfile = (profileData: TunnelProfile | null) => {
    if (project) storeFlag(getTunnelProfileVisibleStorageKey(project.entityId), profileData ? profileData.visible : null);
    setTunnelProfileState(profileData);
  };
  const setTunnelProfileVisible = (visible: boolean) => {
    if (project && tunnelProfile) storeFlag(getTunnelProfileVisibleStorageKey(project.entityId), visible);
    setTunnelProfileState((prev) => (prev ? { ...prev, visible } : prev));
  };
  const setTunnelProfileOpacity = (opacity: number) => {
    const clampedOpacity = Math.max(0.05, Math.min(1, opacity));
    setTunnelProfileState((prev) => (prev ? { ...prev, opacity: clampedOpacity } : prev));
  };

  const setClippingBounds = (bounds: ClippingBounds | null) => setClippingBoundsState(bounds);
  const setShowClippingBounds = (show: boolean) => setShowClippingBoundsState(show);
  const setShowStopeShapes = (show: boolean) => {
    if (project) storeFlag(getShowStopeShapesStorageKey(project.entityId), show);
    setShowStopeShapesState(show);
  };
  const setShowInactiveSpools = (show: boolean) => {
    if (project) storeFlag(getShowInactiveSpoolsStorageKey(project.entityId), show);
    setShowInactiveSpoolsState(show);
  };
  const setStopeShapeOpacity = (opacity: number) => {
    const clamped = clampStopeShapeOpacity(opacity);
    window.localStorage.setItem(STOPE_SHAPE_OPACITY_STORAGE_KEY, String(clamped));
    setStopeShapeOpacityState(clamped);
  };
  const setStopeShapeAnimationStep = (step: AnimationStep) => setStopeShapeAnimationStepState(step);
  const setStopePourData = (data: StopePourData) => setStopePourDataState(data);
  const setStopeFillState = (state: StopeFillState | null) => setStopeFillStateInternal(state);

  const setSpoolColoursByPipe = (show: boolean) => {
    showPipesRef.current = show;
    setShowPipes(show);
    setSpools((prev) => {
      if (!prev) return prev;

      const next: Record<string, PCSpool> = { ...prev };

      Object.entries(next).forEach(([id, spool]) => {
        const parsed = parseInt(spool.pipe.pipeId as unknown as string, 10);
        const idx = Number.isFinite(parsed) ? Math.max(0, parsed - 1) : 0;
        const pipeColour = spool.pipe.colour || GConstants.PIPE_COLOURS[idx] || PCConstants.SPOOL_COLOUR;

        const newColor = show ? pipeColour : PCConstants.SPOOL_COLOUR;

        if ((spool as any)?.changeColour && typeof (spool as any).changeColour === 'function') {
          (spool as any).changeColour(newColor);
        } else {
          (next[id] as any).spoolColour = newColor;
        }
      });

      return next;
    });
  };

  const clearAllPipeColors = () => {
    if (!spools) return;
    const updates = Object.keys(spools).map((spoolId) => ({ spoolId, spoolColour: PCConstants.SPOOL_COLOUR }));
    batchUpdateSpoolAppearance(updates);
  };

  const setNodeConstraint = (nodeId: string, constrained: boolean) => setNodeProperty(nodeId, 'showConstraintArrow', constrained);
  const setSpoolConstraint = (spoolId: string, constrained: boolean) => setSpoolProperty(spoolId, 'showConstraintArrow', constrained);

  const addNode = (node: PCNode) => {
    setNodes((prev) => {
      if (!prev) return { [node.id]: node };
      return { ...prev, [node.id]: node };
    });
  };

  const addNodes = (nodesToAdd: PCNode[]) => {
    if (nodesToAdd.length === 0) return;
    setNodes((prev) => {
      const updated: Record<string, PCNode> = prev ? { ...prev } : {};
      nodesToAdd.forEach((node) => { updated[node.id] = node; });
      return updated;
    });
  };

  const addSpool = (spool: PCSpool) => {
    setSpools((prev) => {
      if (!prev) return { [spool.spoolId]: spool };
      return { ...prev, [spool.spoolId]: spool };
    });
  };

  const addSpools = (spoolsToAdd: PCSpool[]) => {
    if (spoolsToAdd.length === 0) return;
    setSpools((prev) => {
      const updated: Record<string, PCSpool> = prev ? { ...prev } : {};
      spoolsToAdd.forEach((spool) => { updated[spool.spoolId] = spool; });
      return updated;
    });
  };

  const removeSpools = (spoolsToRemove: PCSpool[]) => {
    setSpools((prev) => {
      if (!prev) return prev;
      const updated = { ...prev };
      spoolsToRemove.forEach((spool) => {
        delete updated[spool.spoolId];
      });
      return updated;
    });
  };

  const removeNodes = (nodesToRemove: PCNode[]) => {
    setNodes((prev) => {
      if (!prev) return prev;
      const updated = { ...prev };
      nodesToRemove.forEach((node) => {
        delete updated[node.id];
      });
      return updated;
    });

    setStopes((prev) => {
      if (!prev) return prev;
      const updatedStopes = { ...prev };
      nodesToRemove.forEach((node) => {
        delete updatedStopes[node.id];
      });
      return updatedStopes;
    });
  };

  const handleUnsetSelectedStope = () => {
    selectedStopeRef.current = undefined;
    setSelectedStope(undefined);
    setRoutes(undefined);
    if (spools) {
      Object.values(spools).forEach((spool) => {
        spool.changeColour(showPipesRef.current ? resolvePipeColour(spool) : PCConstants.SPOOL_COLOUR);
        if (spool.showConstraintArrow) {
          setSpoolConstraint(spool.spoolId, false);
        }
      });
    }
    Object.values(nodes || {}).forEach((node) => {
      if (node.showConstraintArrow) {
        setNodeSize(node.id, PCConstants.NODE_RADIUS);
        setNodeColour(node.id, PCConstants.NODE_COLOUR);
        setNodeConstraint(node.id, false);
      }
    });
  };

  const filteredRoutesRef = useRef(filteredRoutes);
  const filteredNodesRef = useRef(filteredNodes);
  const filteredStopesRef = useRef(filteredStopes);
  const filteredSpoolsRef = useRef(filteredSpools);
  const filteredPipesRef = useRef(filteredPipes);

  filteredRoutesRef.current = filteredRoutes;
  filteredNodesRef.current = filteredNodes;
  filteredStopesRef.current = filteredStopes;
  filteredSpoolsRef.current = filteredSpools;
  filteredPipesRef.current = filteredPipes;

  const model = useMemo(() => ({
    routes: filteredRoutes,
    nodes: filteredNodes,
    stopes: filteredStopes,
    spools: filteredSpools,
    pipes: filteredPipes,
    importedSolids,
    tunnelProfile,
    clippingBounds,
    showClippingBounds,
    showStopeShapes,
    showInactiveSpools,
    levelAreaFilter,
    spoolDateFilter,
    stopeShapeDateRange,
    stopeShapeTimelineDay,
    stopeShapeOpacity,
    stopeShapeAnimationStep,
    stopePourData,
    stopeLegendConfig,
    stopeFillState,
    stopeLocations: stopeLocations || undefined,
    mineOverview: mineOverview || [],
    project: project!,
    selectedStope,
    selectedNode,
    selectedSpool,
    activeBackfillPlant: activeBackfillPlant!,
    rheology: fullRheologyData || [],
    rheologySummaries: rheologySummaries || [],
    recipeState,
    isUpdating: isUpdatingRef.current,
    isNetworkFiltered: (['nodes', 'spools', 'pipes'] as ModelPart[]).some((part) => Object.keys(modelFilters[part] ?? {}).length > 0),
    optimiserResults,
    loadGeneration,
    addFilter,
    removeFilter,
    setRoutes,
    getSelectedRoute,
    setSelectedRoute: handleSelectedRoute,
    setSelectedStope: handleSetSelectedStope,
    unsetSelectedStope: handleUnsetSelectedStope,
    setSelectedNode: handleSetSelectedNode,
    setSelectedSpool,
    setActiveBackfillPlant: handleSetActiveBackfillPlant,
    showPipes,
    setSpoolColoursByPipe,
    resolveSpoolBaseColour: (spool: PCSpool) => (showPipes ? resolvePipeColour(spool) : PCConstants.SPOOL_COLOUR),
    clearAllPipeColors,
    setNodeProperty,
    setSpoolProperty,
    setNodeProperties,
    setSpoolProperties,
    setNodeColour,
    setNodeColours,
    setSpoolColour,
    setNodeSize,
    batchUpdateNodeAppearance,
    batchUpdateSpoolAppearance,
    setSpoolInspectionPoints,
    clearAllInspectionPoints,
    setNodeConstraint,
    setSpoolConstraint,
    unsetNodeAsStope,
    unsetNodeAsPit,
    setNodeAsStope,
    setNodeAsPit,
    setRecipeState: handleSetRecipeState,
    setOptimiserResults,
    findSpoolsByNodeId,
    findSpoolByPair,
    findNodeById,
    addNode,
    addNodes,
    addSpool,
    addSpools,
    removeNodes,
    removeSpools,
    addImportedSolid,
    removeImportedSolid,
    clearImportedSolids,
    setTunnelProfile,
    setTunnelProfileVisible,
    setTunnelProfileOpacity,
    setClippingBounds,
    setShowClippingBounds,
    setShowStopeShapes,
    setShowInactiveSpools,
    setLevelAreaFilter,
    setSpoolDateFilter,
    setStopeShapeDateRange,
    setStopeShapeTimelineDay,
    setStopeShapeOpacity,
    setStopeShapeAnimationStep,
    setStopePourData,
    setStopeLegendConfig,
    setStopeFillState,
  }), [
    filteredRoutes,
    filteredNodes,
    filteredStopes,
    filteredSpools,
    filteredPipes,
    importedSolids,
    tunnelProfile,
    clippingBounds,
    showClippingBounds,
    showStopeShapes,
    showInactiveSpools,
    levelAreaFilter,
    spoolDateFilter,
    stopeShapeDateRange,
    stopeShapeTimelineDay,
    stopeShapeOpacity,
    stopeShapeAnimationStep,
    stopePourData,
    stopeLegendConfig,
    stopeFillState,
    stopeLocations,
    mineOverview,
    project,
    selectedStope,
    selectedNode,
    selectedSpool,
    activeBackfillPlant,
    fullRheologyData,
    rheologySummaries,
    recipeState,
    showPipes,
    loadGeneration,
    modelFilters,
  ]);

  React.useEffect(() => {
    if (!model.recipeState || !model.routes || Object.keys(model.routes).length === 0) return;
    const routeToUse = model.getSelectedRoute() ?? returnShortestRoute(model.routes);
    if (!routeToUse || routeToUse.pumpPressureKpa !== undefined) return;

    const recalculatedRoute = calculateFrictionForRoute(model, model.recipeState, routeToUse);
    if (recalculatedRoute) {
      model.setSelectedRoute(recalculatedRoute);
    }
  }, [model.routes]);

  const updateMineOverview = React.useCallback((overview: StopeBackfillSummaryDto[]) => {
    setMineOverview(overview);
  }, []);

  return {
    model,
    initModelState,
    updateMineOverview,
    loaded,
  };
};

export default useModelState;
export type { Model, ModelState };
